import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { Stream } from '@cloudflare/stream-react';

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadLessons = async () => {
    try {
      const response = await coursesAPI.getCourseLessons(courseId);
      setLessons(response.data);
      if (response.data.length > 0) {
        handleSelectLesson(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLesson = async (lesson) => {
    setSelectedLesson(lesson);
    setVideoUrl(null);
    
    // Load signed video URL
    if (lesson.video_id) {
      setLoadingVideo(true);
      try {
        const response = await coursesAPI.getLessonVideoUrl(lesson.id);
        setVideoUrl(response.data.videoUrl);
      } catch (error) {
        console.error('Error loading video URL:', error);
      } finally {
        setLoadingVideo(false);
      }
    }
  };

  const handleCompleteLesson = async (lessonId) => {
    try {
      await coursesAPI.completeLesson(lessonId);
      loadLessons();
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt Lektionen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              ← Zurück
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Kurs Lektionen</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lessons Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Lektionen</h2>
              <div className="space-y-2">
                {lessons.map((lesson, index) => (
                  <button
                    key={lesson.id}
                    onClick={() => handleSelectLesson(lesson)}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      selectedLesson?.id === lesson.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">Lektion {index + 1}</span>
                      {lesson.completed && (
                        <span className={selectedLesson?.id === lesson.id ? 'text-white' : 'text-green-600'}>
                          ✓
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${selectedLesson?.id === lesson.id ? 'text-white/90' : 'text-gray-600'}`}>
                      {lesson.title}
                    </p>
                    {lesson.duration_minutes > 0 && (
                      <p className={`text-xs mt-1 ${selectedLesson?.id === lesson.id ? 'text-white/70' : 'text-gray-500'}`}>
                        {lesson.duration_minutes} Min.
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Video Player & Content */}
          <div className="lg:col-span-2">
            {selectedLesson ? (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Video Player */}
                <div className="bg-gray-900 aspect-video">
                  {selectedLesson.video_id ? (
                    <Stream 
                      controls 
                      src={selectedLesson.video_id}
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-center text-white">
                      <div>
                        <div className="text-6xl mb-4">🎥</div>
                        <p className="text-xl">Video folgt in Kürze</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lesson Info */}
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedLesson.title}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {selectedLesson.description}
                  </p>

                  {/* Complete Button */}
                  {!selectedLesson.completed ? (
                    <button
                      onClick={() => handleCompleteLesson(selectedLesson.id)}
                      className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg"
                    >
                      Als abgeschlossen markieren
                    </button>
                  ) : (
                    <div className="bg-green-50 text-green-700 py-3 px-4 rounded-lg text-center font-medium">
                      ✓ Abgeschlossen am {new Date(selectedLesson.completed_at).toLocaleDateString('de-DE')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Wähle eine Lektion</h3>
                <p className="text-gray-600">Klicke auf eine Lektion um zu starten!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
